/**
 * Le conseil du coach, mis en phrase.
 *
 * `shared/coach.ts` décide — quoi garder, combien relancer, ce qu'il faut battre et à
 * quel point c'est dur. Il ne rend plus de phrase depuis l'i18n, et c'est mieux ainsi :
 * une phrase ne se traduit pas depuis un moteur de règles, et elle ne se teste qu'en
 * la comparant à elle-même. Ici on habille, et rien de plus.
 *
 * ⚠️ Le chiffre traverse tel quel. Le coach ne doit **jamais mentir par
 * approximation** : « une fois sur 8 » vient du dénombrement exact des 216 tirages, et
 * l'habillage ne l'arrondit pas. Les deux bouts — « jamais » et « presque à tous les
 * coups » — ont leur propre phrase parce que « une fois sur l'infini » n'apprend rien.
 */

import type { Conseil } from "@zinc421/shared";

import type { Textes } from "../i18n/index.js";

/** « le 6, le 5 et le 1 » — l'énumération est une affaire de langue. */
function enumerer(valeurs: readonly number[], textes: Textes): string {
  const morceaux = valeurs.map((v) => textes.coachDe(v));
  if (morceaux.length <= 1) return morceaux[0] ?? "";
  return textes.coachEtPuis(
    morceaux.slice(0, -1).join(", "), morceaux.at(-1)!);
}

export function phraseDuConseil(conseil: Conseil, textes: Textes): string {
  const debut = (() => {
    switch (conseil.motif) {
      case "poser": return textes.coachPoser(conseil.main);
      case "arreter": return textes.coachArreter(conseil.main);
      case "toutRelancer": return textes.coachToutRelancer(conseil.main);
      default:
        return textes.coachGarder(
          enumerer(conseil.garder, textes), textes.des(conseil.aRelancer));
    }
  })();

  const enjeu = conseil.phase === "DECHARGE"
    ? textes.coachEnjeuDecharge : textes.coachEnjeuCharge;

  const objectif = conseil.objectif;
  if (!objectif) return `${debut} ${enjeu}`;
  const aBattre = objectif.uneFoisSur !== null
    ? textes.coachObjectif(objectif.cle, objectif.uneFoisSur)
    : objectif.probabilite <= 0
      ? textes.coachObjectifJamais(objectif.cle)
      : textes.coachObjectifToujours(objectif.cle);
  return `${debut} ${enjeu} ${aBattre}`;
}
